/* Product-local scroll animation. No Shopify API, cart, product, or theme writes. */
(() => {
  'use strict';
  if (customElements.get('ibolt-product-motion')) return;

  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const PROFILES = {
    arm: [
      { at: 0, nav: 0, title: 'Together.', label: '01 / THE COMPLETE ARM', copy: 'One arm. More ways to position your device.' },
      { at: .245, nav: 1, title: 'Apart.', label: '02 / EVERY PART. A PURPOSE.', copy: 'See how the arm’s components connect.' },
      { at: .485, nav: 1, title: 'Together again.', label: '03 / MADE TO CONNECT', copy: 'Bring the arm back together.' },
      { at: .615, nav: 2, title: 'Find your angle.', label: '04 / ADJUSTABLE POSITIONING', copy: 'Explore the articulated arm from every angle.' },
      { at: .825, nav: 3, title: 'Ready for work.', label: '05 / READY FOR YOUR SETUP', copy: 'Ready to become part of your setup.' }
    ],
    system: [
      { at: 0, nav: 0, title: 'Together.', label: '01 / THE COMPLETE SYSTEM', copy: 'One mount. Ready for work.' },
      { at: .245, nav: 1, title: 'Apart.', label: '02 / EVERY PART. A PURPOSE.', copy: 'Every part has a purpose. Explore how the system connects.' },
      { at: .485, nav: 1, title: 'Together again.', label: '03 / MADE TO CONNECT', copy: 'Bring the mount back together.' },
      { at: .615, nav: 2, title: 'Find your angle.', label: '04 / ADJUSTABLE POSITIONING', copy: 'An articulated arm and ball joints put your device in position.' },
      { at: .825, nav: 3, title: 'Ready for work.', label: '05 / YOUR DEVICE. IN VIEW.', copy: 'Make your device work as hard as you do.' }
    ],
    adapter: [
      { at: 0, nav: 0, title: 'Every angle.', label: '01 / ROTATE', copy: 'A closer look, from every side.' },
      { at: .28, nav: 1, title: 'The AMPS plate.', label: '02 / THE MOUNTING PATTERN', copy: 'Explore the mounting plate and its hole pattern.' },
      { at: .72, nav: 2, title: 'The ball connection.', label: '03 / THE BALL', copy: 'See the ball connection from every angle.' },
      { at: .92, nav: 3, title: 'Ready to connect.', label: '04 / READY FOR YOUR SETUP', copy: 'One connection in your mounting system.' }
    ]
  };

  class IboltProductMotion extends HTMLElement {
    connectedCallback() { queueMicrotask(() => { if (this.isConnected) this.mount(); }); }
    disconnectedCallback() { this.unmount(); }

    mount() {
      if (this.mounted) return;
      // Defense in depth for local fixtures and accidental client-side reuse.
      if (this.dataset.productId && this.dataset.targetProductId && this.dataset.productId !== this.dataset.targetProductId) {
        this.hidden = true;
        return;
      }
      const q = (selector) => this.querySelector(selector);
      this.video = q('[data-ibm-video]');
      this.range = q('[data-ibm-range]');
      this.track = q('[data-ibm-track]');
      this.stage = q('[data-ibm-stage]');
      if (!this.video || !this.range || !this.track || !this.stage) return;
      this.mounted = true;
      this.abort = new AbortController();
      this.buttons = [...this.querySelectorAll('[data-ibm-jump]')];
      this.heading = q('[data-ibm-heading]');
      this.description = q('[data-ibm-description]');
      this.label = q('[data-ibm-label]');
      this.percentage = q('[data-ibm-percentage]');
      this.status = q('[data-ibm-status]');
      this.hint = q('[data-ibm-hint]');
      this.retry = q('[data-ibm-retry]');
      this.profile = (PROFILES[this.dataset.mode] || PROFILES.arm).map((chapter) => ({ ...chapter }));
      if (this.dataset.intro) this.profile[0].copy = this.dataset.intro;
      this.fps = Number(this.dataset.fps) === 12 ? 12 : 24;
      this.editor = this.dataset.editor === 'true';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.target = 0;
      this.lastChapter = -1;
      this.raf = 0;
      this.loaded = false;
      this.started = false;
      this.busy = false;
      this.near = false;
      this.failed = false;
      this.seekCount = 0;
      this.video.muted = this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.autoplay = false;
      this.video.removeAttribute('autoplay');
      this.video.controls = false;
      this.video.pause();
      this.classList.add('is-enhanced');

      const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      listen(window, 'scroll', () => { if (this.near && !this.manual) this.schedule(); }, { passive: true });
      listen(window, 'resize', () => this.schedule(), { passive: true });
      listen(window, 'pageshow', () => this.schedule());
      listen(document, 'visibilitychange', () => { if (!document.hidden) this.schedule(); });
      listen(this.video, 'loadedmetadata', () => this.onReady());
      listen(this.video, 'loadeddata', () => this.onReady());
      listen(this.video, 'canplay', () => this.onReady());
      listen(this.video, 'progress', () => this.schedule(false));
      listen(this.video, 'seeked', () => {
        this.busy = false;
        clearTimeout(this.seekTimer);
        this.setStatus('');
        this.schedule(false);
      });
      listen(this.video, 'error', () => this.onError());
      listen(this.video, 'play', () => this.video.pause());
      listen(this.range, 'input', () => this.goTo(Number(this.range.value) / 1000, false));
      this.buttons.forEach((button) => listen(button, 'click', () => this.goTo(Number(button.dataset.ibmJump), true)));
      if (this.retry) listen(this.retry, 'click', () => {
        this.failed = false;
        this.started = false;
        this.loaded = false;
        this.classList.remove('is-unavailable');
        this.retry.hidden = true;
        this.ensureLoaded();
      });
      this.onPreference = () => this.setMotionMode();
      if (this.motionQuery.addEventListener) this.motionQuery.addEventListener('change', this.onPreference);
      else this.motionQuery.addListener(this.onPreference);
      this.setMotionMode();
      this.updateCopy();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.near = entries[0].isIntersecting;
          if (this.near) {
            this.ensureLoaded();
            this.schedule();
          }
        }, { rootMargin: '600px 0px' });
        this.observer.observe(this.track);
      } else {
        this.near = true;
        this.ensureLoaded();
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        this.resizeObserver.observe(this.stage);
        this.resizeObserver.observe(this.track);
      }
      this.schedule();
    }

    setMotionMode() {
      this.manual = this.editor || this.motionQuery.matches;
      this.classList.toggle('is-manual', this.manual);
      if (this.hint) this.hint.textContent = this.manual ? 'Use the slider or chapter buttons to explore.' : 'Scroll to explore. Scroll back to reverse.';
      this.schedule();
    }

    ensureLoaded() {
      if (!this.mounted || this.started || this.failed) return;
      this.started = true;
      this.video.preload = 'auto';
      this.video.load();
      this.loadTimer = setTimeout(() => {
        if (this.mounted && !this.loaded && !this.failed) this.setStatus('Preparing the product view…');
      }, 650);
    }

    onReady() {
      if (!this.mounted || !Number.isFinite(this.video.duration) || this.video.duration <= 0) return;
      this.loaded = true;
      clearTimeout(this.loadTimer);
      this.range.disabled = false;
      this.buttons.forEach((button) => { button.disabled = false; });
      this.setStatus('');
      this.schedule(false);
    }

    setStatus(message) { if (this.status && this.status.textContent !== message) this.status.textContent = message; }

    onError() {
      if (!this.mounted) return;
      this.failed = true;
      this.busy = false;
      clearTimeout(this.seekTimer);
      clearTimeout(this.loadTimer);
      this.classList.add('is-unavailable');
      this.range.disabled = true;
      this.buttons.forEach((button) => { button.disabled = true; });
      if (this.retry) this.retry.hidden = false;
      this.setStatus('This animation could not load. You can retry or continue to the product details.');
    }

    geometry() {
      const top = Number.parseFloat(getComputedStyle(this.stage).top) || 0;
      const bounds = this.track.getBoundingClientRect();
      return { top: bounds.top + window.scrollY - top, distance: Math.max(1, this.track.offsetHeight - this.stage.offsetHeight) };
    }

    goTo(progress, smooth) {
      progress = clamp(Number.isFinite(progress) ? progress : 0);
      this.ensureLoaded();
      if (this.manual || this.failed) {
        this.target = progress;
        this.schedule(false);
        return;
      }
      const geometry = this.geometry();
      // Slider changes are immediate. Button jumps animate the page; scroll
      // events then drive the latest video target without a seek-to-end flash.
      window.scrollTo({ top: geometry.top + geometry.distance * progress, behavior: smooth ? 'smooth' : 'instant' });
      if (!smooth) {
        this.target = progress;
        this.schedule(false);
      }
    }

    schedule(readScroll = true) {
      if (!this.mounted) return;
      this.readScroll = this.readScroll || readScroll;
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        if (!this.mounted) return;
        if (this.readScroll && !this.manual && this.near && !this.failed) {
          const geometry = this.geometry();
          this.target = clamp((window.scrollY - geometry.top) / geometry.distance);
        }
        this.readScroll = false;
        this.updateCopy();
        this.pumpSeek();
      });
    }

    updateCopy() {
      let index = 0;
      this.profile.forEach((chapter, i) => { if (this.target >= chapter.at) index = i; });
      const chapter = this.profile[index];
      if (this.lastChapter !== index) {
        this.lastChapter = index;
        if (this.heading) this.heading.textContent = chapter.title;
        if (this.description) this.description.textContent = chapter.copy;
        if (this.label) this.label.textContent = chapter.label;
        this.buttons.forEach((button, i) => {
          if (i === chapter.nav) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });
      }
      const percent = Math.round(this.target * 100);
      this.range.value = String(Math.round(this.target * 1000));
      this.range.style.setProperty('--ibm-fill', `${percent}%`);
      this.range.setAttribute('aria-valuetext', `${chapter.title.replace(/\.$/, '')}, ${percent} percent`);
      if (this.percentage) this.percentage.textContent = String(percent).padStart(2, '0');
    }

    pumpSeek() {
      if (!this.mounted || !this.loaded || this.failed || document.hidden || this.busy || this.video.seeking) return;
      const finalFrame = Math.max(0, Math.round(this.video.duration * this.fps) - 1);
      const time = Math.round(this.target * finalFrame) / this.fps;
      if (Math.abs(this.video.currentTime - time) < .45 / this.fps) return;
      this.busy = true;
      this.seekCount += 1;
      try {
        // Never assign currentTime again until this seek finishes. Rapid input
        // only replaces `target`, so stale intermediate positions are dropped.
        this.video.currentTime = time;
      } catch (_) {
        this.busy = false;
        this.setStatus('Preparing this view…');
        return;
      }
      clearTimeout(this.seekTimer);
      this.seekTimer = setTimeout(() => {
        if (!this.mounted || this.failed) return;
        this.busy = false;
        if (this.video.seeking) this.setStatus('Preparing this view…');
        else this.schedule(false);
      }, 2500);
    }

    unmount() {
      if (!this.mounted) return;
      this.mounted = false;
      this.abort.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.motionQuery.removeEventListener) this.motionQuery.removeEventListener('change', this.onPreference);
      else this.motionQuery.removeListener(this.onPreference);
      cancelAnimationFrame(this.raf);
      clearTimeout(this.seekTimer);
      clearTimeout(this.loadTimer);
      this.video.pause();
      this.video.controls = true;
      this.classList.remove('is-enhanced', 'is-manual', 'is-unavailable');
    }
  }

  customElements.define('ibolt-product-motion', IboltProductMotion);
  // Custom-element callbacks cover ordinary navigation. These editor hooks also
  // cover Shopify's unload-before-replace lifecycle; each mount is idempotent.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('ibolt-product-motion').forEach((element) => element.mount());
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('ibolt-product-motion').forEach((element) => element.unmount());
  });
})();
